import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, History, X } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/States';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useTableQuery } from '@/hooks/useTableQuery';
import { auditApi } from '@/services/api/endpoints/audit';
import { usersApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AuditEvent, AuditSearchParams } from '@/types/audit';
import { formatDateTime, humanise, orDash } from '@/utils/format';

/** Mirrors the backend's AuditAction enum; the server rejects anything else. */
const ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'TOKEN_REFRESH',
  'PERMISSION_CHANGE',
  'STATUS_CHANGE',
] as const;

type AuditActionFilter = (typeof ACTIONS)[number];

/*
 * Suggestions only. The server matches the entity type exactly and
 * case-sensitively, and new modules add types without telling the browser, so
 * the field stays free text with these offered as a datalist.
 */
const ENTITY_TYPE_SUGGESTIONS = [
  'AdmissionApplication',
  'Announcement',
  'Student',
  'StudentEnrollment',
  'StudentGuardian',
  'User',
  'Role',
  'Invoice',
  'Payment',
  'Refund',
  'Examination',
  'Employee',
  'PayrollPeriod',
  'LeaveRequest',
  'Book',
  'Reservation',
  'LibraryFine',
  'TransportTrip',
  'TransportIncident',
  'AcademicYear',
  'Section',
  'Timetable',
];

const COLUMN_COUNT = 6;

/**
 * The audit trail (read-only).
 *
 * <p>Every filter is applied on the server — the trail is never shipped to the
 * browser whole — and the order is fixed there too, newest first, so no column
 * offers sorting it could not honour.
 *
 * <p>The before/after values are shown exactly as recorded: pretty-printed when
 * they happen to be JSON, verbatim otherwise, and always as text. A recorded
 * value is data about a change, never markup to render.
 */
export function AuditTrailPage() {
  const { can } = useAuth();
  const table = useTableQuery(undefined, 25);

  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState<AuditActionFilter | ''>('');
  const [actorUserId, setActorUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const viewsUsers = can(PERMISSIONS.USER_VIEW);

  const params: AuditSearchParams = {
    page: table.page,
    size: table.size,
    ...(entityType.trim() === '' ? {} : { entityType: entityType.trim() }),
    ...(action === '' ? {} : { action }),
    ...(actorUserId === '' ? {} : { actorUserId }),
    ...(from === '' ? {} : { from: startOfDay(from) }),
    ...(to === '' ? {} : { to: endOfDay(to) }),
  };

  const events = useQuery({
    queryKey: queryKeys.audit.list(params),
    queryFn: () => auditApi.list(params),
    placeholderData: (previous) => previous,
  });

  const users = useQuery({
    queryKey: queryKeys.users.list({ page: 0, size: 100, sort: 'lastName,asc' }),
    queryFn: () => usersApi.list({ page: 0, size: 100, sort: 'lastName,asc' }),
    enabled: viewsUsers,
  });

  const filtered =
    entityType !== '' || action !== '' || actorUserId !== '' || from !== '' || to !== '';

  const clearFilters = () => {
    setEntityType('');
    setAction('');
    setActorUserId('');
    setFrom('');
    setTo('');
    table.setPage(0);
  };

  const rows = events.data?.data;

  return (
    <ListPage
      title="Audit trail"
      description="Who did what, and when. Recorded by the server on every change; nothing here can be edited."
      icon={<History className="h-5 w-5" aria-hidden="true" />}
      filters={
        <>
          <label className="flex items-center gap-2 text-sm text-muted">
            <span className="sr-only">Entity type</span>
            <input
              type="text"
              list="audit-entity-types"
              value={entityType}
              placeholder="Entity type, e.g. Student"
              onChange={(event) => {
                setEntityType(event.target.value);
                table.setPage(0);
              }}
              className="w-full min-w-0 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink shadow-xs transition-colors placeholder:text-subtle hover:border-line-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)] sm:w-52"
            />
            <datalist id="audit-entity-types">
              {ENTITY_TYPE_SUGGESTIONS.map((type) => (
                <option key={type} value={type} />
              ))}
            </datalist>
          </label>
          <FilterSelect
            label="Action"
            value={action}
            onChange={(value) => {
              setAction(value);
              table.setPage(0);
            }}
            options={ACTIONS.map((value) => ({ value, label: humanise(value) }))}
          />
          {viewsUsers && (
            <FilterSelect
              label="Actor"
              value={actorUserId}
              onChange={(value) => {
                setActorUserId(value);
                table.setPage(0);
              }}
              options={(users.data?.data ?? []).map((user) => ({
                value: user.id,
                label: user.fullName,
              }))}
            />
          )}
          <FilterDate
            label="From"
            value={from}
            onChange={(value) => {
              setFrom(value);
              table.setPage(0);
            }}
          />
          <FilterDate
            label="To"
            value={to}
            onChange={(value) => {
              setTo(value);
              table.setPage(0);
            }}
          />
          {filtered && (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Clear
            </Button>
          )}
        </>
      }
      pagination={events.data?.pagination}
      onPageChange={table.setPage}
      onSizeChange={table.setSize}
    >
      {events.error !== null ? (
        <ErrorState error={events.error} onRetry={() => void events.refetch()} />
      ) : rows === undefined ? (
        <TableSkeleton columns={COLUMN_COUNT} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No audit events"
          description={
            filtered ? 'Nothing recorded matches these filters.' : 'Nothing has been recorded yet.'
          }
          icon={<History className="h-6 w-6" aria-hidden="true" />}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-2">
                <th scope="col" className="w-10 px-2 py-3">
                  <span className="sr-only">Details</span>
                </th>
                {['When', 'Actor', 'Action', 'Entity', 'Outcome'].map((header) => (
                  <th
                    key={header}
                    scope="col"
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className={clsx(
                'divide-y divide-line',
                events.isFetching && 'pointer-events-none opacity-60 transition-opacity',
              )}
            >
              {rows.map((event) => {
                const open = expanded === event.id;
                return (
                  <Fragment key={event.id}>
                    <tr
                      className="cursor-pointer transition-colors duration-100 hover:bg-surface-2"
                      onClick={() => setExpanded(open ? null : event.id)}
                    >
                      <td className="px-2 py-3 align-top">
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-controls={`audit-${event.id}`}
                          aria-label={open ? 'Hide details' : 'Show details'}
                          onClick={(click) => {
                            click.stopPropagation();
                            setExpanded(open ? null : event.id);
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-3 hover:text-ink"
                        >
                          {open ? (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top tabular-nums text-muted">
                        {formatDateTime(event.occurredAt)}
                      </td>
                      <td className="px-4 py-3 align-top text-muted">
                        <span className="block max-w-[16rem] truncate text-ink" title={event.actorEmail}>
                          {event.actorEmail ?? 'System'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-muted">
                        {humanise(event.action)}
                      </td>
                      <td className="px-4 py-3 align-top text-muted">
                        <span className="block text-ink">{orDash(event.entityType)}</span>
                        {event.entityId !== undefined && (
                          <span className="block max-w-[16rem] truncate font-mono text-[11px] text-subtle">
                            {event.entityId}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <OutcomeBadge outcome={event.outcome} />
                      </td>
                    </tr>
                    {open && (
                      <tr id={`audit-${event.id}`} className="bg-surface-2/60">
                        <td colSpan={COLUMN_COUNT} className="px-4 py-4">
                          <EventDetail event={event} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

/** The server records SUCCESS or FAILURE; anything else is shown as sent. */
function OutcomeBadge({ outcome }: { outcome?: string }) {
  if (outcome === undefined) {
    return <span className="text-muted">—</span>;
  }
  const tone = outcome === 'SUCCESS' ? 'success' : outcome === 'FAILURE' ? 'danger' : 'neutral';
  return (
    <Badge tone={tone}>
      <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
      {outcome.toLowerCase()}
    </Badge>
  );
}

function EventDetail({ event }: { event: AuditEvent }) {
  return (
    <div className="space-y-3">
      <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2">
        {event.message !== undefined && (
          <div className="sm:col-span-2">
            <dt className="text-xs text-subtle">Message</dt>
            <dd className="break-words text-ink">{event.message}</dd>
          </div>
        )}
        <div>
          <dt className="text-xs text-subtle">IP address</dt>
          <dd className="font-mono text-ink">{orDash(event.ipAddress)}</dd>
        </div>
        <div>
          <dt className="text-xs text-subtle">Actor ID</dt>
          <dd className="break-all font-mono text-ink">{orDash(event.actorUserId)}</dd>
        </div>
      </dl>

      {event.oldValue === undefined && event.newValue === undefined ? (
        <p className="text-xs text-muted">This operation recorded no before or after values.</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          <ValueBlock label="Before" value={event.oldValue} />
          <ValueBlock label="After" value={event.newValue} />
        </div>
      )}
    </div>
  );
}

function ValueBlock({ label, value }: { label: string; value?: string }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs text-subtle">{label}</p>
      {value === undefined ? (
        <p className="border border-dashed border-line px-3 py-2 text-xs text-muted">Not recorded</p>
      ) : (
        // Rendered as a text node inside <pre>: React escapes it, so a value
        // that happens to contain markup is shown, never interpreted.
        <pre className="max-h-80 overflow-auto rounded-lg border border-line bg-surface px-3 py-2 font-mono text-[12px] leading-relaxed whitespace-pre-wrap break-words text-ink">
          {prettyJson(value)}
        </pre>
      )}
    </div>
  );
}

/** Pretty-prints JSON when the value parses as JSON; returns it untouched otherwise. */
function prettyJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** A calendar date in the viewer's timezone, as the instant that day starts. */
function startOfDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0).toISOString();
}

/** The last millisecond of a calendar date, so "to" includes the whole day. */
function endOfDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, 23, 59, 59, 999).toISOString();
}

/** A compact date filter for the list toolbar, styled like FilterSelect. */
function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted">
      <span className="text-xs uppercase tracking-[0.04em]">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink shadow-xs transition-colors hover:border-line-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
      />
    </label>
  );
}
